import {
  FieldDefinition,
  FieldBuilder,
  MenuCategories,
  MenuSubcategories,
  MENU_CATEGORY_KEY,
  MENU_SUBCATEGORY_KEY,
  MENU_NAME_SUFFIX,
  IActionsSchema,
  modalAction,
  ActionOverRecordTypes,
  ZendraLocalizationServices,
  IModal,
  createImportExcelModalConfig,
  ZnFileUploader,
  triggerAction,
  ZnExcelImportErrorsModal,
  ExcelImportErrorsExportLike,
  ExcelImportResultLike,
} from '@zendra/shared';
import { Filters } from 'angular-slickgrid';
import { DepartmentService } from '../proxy/planning/controllers/department.service';
import { LocationService } from '../proxy/planning/controllers/location.service';
import { PropertyTypeService } from '../proxy/planning/controllers/property-type.service';
import { CadastralSectionService } from '../proxy/planning/controllers/cadastral-section.service';
import { ForestryManagementService } from '../proxy/planning/controllers/forestry-management.service';
import { PropertyService, RegisterService } from '@planning/proxy/planning/controllers';
import { signal } from '@angular/core';

FieldBuilder.setNamespace('Planning');

export const REGISTER_FIELDS: FieldDefinition[] = [
  FieldBuilder
    .text('id', 'Entity.Register.Id')
    .readOnly()
    .hideInChildForms()
    .build(),
  FieldBuilder
    .text('name', 'Entity.Register.Name')
    .required()
    .minLength(1)
    .maxLength(30)
    .build(),
  FieldBuilder
    .number('documentaryArea', 'Entity.Register.DocumentaryArea')
    .required()
    .decimal(4,10)
    .build(),
  FieldBuilder
    .number('standArea', 'Entity.Register.StandArea')
    .required()
    .decimal(2,10)
    .build(),
  FieldBuilder
    .number('otherUseArea', 'Entity.Register.OtherUseArea')
    .required()
    .decimal(2,10)
    .build(),
  FieldBuilder
    .number('exoneratedArea', 'Entity.Register.ExoneratedArea')
    .required()
    .decimal(4,10)
    .build(),
  FieldBuilder
    .checkbox('isTaxIncentiveApplied', 'Entity.Register.IsTaxIncentiveApplied')
    .build(),
  FieldBuilder
    .number('coneatIndex', 'Entity.Register.ConeatIndex')
    .required()
    .decimal(4,10)
    .build(),
  FieldBuilder
    .checkbox('isFscCertified', 'Entity.Register.IsFscCertified')
    .build(),
  FieldBuilder
    .checkbox('isPefcCertified', 'Entity.Register.IsPefcCertified')
    .build(),
  FieldBuilder
    .number('officialCertifiedArea', 'Entity.Register.OfficialCertifiedArea')
    .required()
    .decimal(4,10)
    .build(),
  FieldBuilder
    .text('description', 'Entity.Register.Description')
    .maxLength(100)
    .build(),
  FieldBuilder
    .date('purchaseDate', 'Entity.Register.PurchaseDate')
    .build(),
  FieldBuilder
    .date('deedDate', 'Entity.Register.DeedDate')
    .build(),
  FieldBuilder
    .number('deedValue', 'Entity.Register.DeedValue')
    .required()
    .decimal(2,15)
    .build(),
  FieldBuilder
    .foreignKey('policeCadastralSectionId', 'Entity.Register.PoliceCadastralSection', CadastralSectionService)
    .displayFromNested('policeCadastralSection.name')
    .sortBy('policeCadastralSection.name')
    .build(),
  FieldBuilder
    .foreignKey('judicialCadastralSectionId', 'Entity.Register.JudicialCadastralSection', CadastralSectionService)
    .displayFromNested('judicialCadastralSection.name')
    .sortBy('judicialCadastralSection.name')
    .build(),
  FieldBuilder
    .date('ownershipDate', 'Entity.Register.OwnershipDate')
    .build(),
  FieldBuilder
    .foreignKey('propertyId', 'Entity.Property', PropertyService)
    .displayFromNested('property.name')
    .sortBy('property.name')
    .build(),
  FieldBuilder
    .text('previousName', 'Entity.Register.PreviousName')
    .maxLength(100)
    .build(),
  FieldBuilder
    .date('fscCertificationDate', 'Entity.Register.FscCertificationDate')
    .build(),
  FieldBuilder
    .foreignKey('fscForestryManagementId', 'Entity.Register.FscForestryManagement', ForestryManagementService)
    .display('certificationCode.name')
    .build(),
  FieldBuilder
    .foreignKey('pefcForestryManagementId', 'Entity.Register.PefcForestryManagement', ForestryManagementService)
    .display('certificationCode.name')
    .build(),
  FieldBuilder
    .date('pefcCertificationDate', 'Entity.Register.PefcCertificationDate')
    .build(),
  FieldBuilder
    .text('notes', 'Entity.Register.Notes')
    .maxLength(200)
    .build(),
  FieldBuilder
    .number('fiscalValue', 'Entity.Register.FiscalValue')
    .decimal(2,15)
    .build(),
  FieldBuilder
    .text('dicose', 'Entity.Register.Dicose')
    .maxLength(15)
    .build(),
  FieldBuilder
    .checkbox('exemptsTaxes', 'Entity.Register.ExemptsTaxes')
    .build(),
  FieldBuilder
    .foreignKey('departmentId', 'Entity.Department', DepartmentService)
    .required()
    .displayFromNested('department.name')
    .sortBy('department.name')
    .build(),
  FieldBuilder
    .foreignKey('locationId', 'Entity.Location', LocationService)
    .required()
    .displayFromNested('location.name')
    .sortBy('location.name')
    .hideInChildForms()
    .build(),
  FieldBuilder
    .foreignKey('propertyTypeId', 'Entity.PropertyType', PropertyTypeService)
    .required()
    .displayFromNested('propertyType.name')
    .sortBy('propertyType.name')
    .build(),
  FieldBuilder
    .foreignKey('propertyTypeId', 'Entity.PropertyType.IsFOManagement', PropertyTypeService)
    .displayFromNested('propertyType.isFOManagement')
    .displayAsCheckbox()
    .filterBy('propertyTypeIsFOManagement')
    .customFilter((localizationService: any) => ({
      model: Filters.singleSelect,
      collection: [
        { value: true, label: localizationService.instant('LanguageService::True') },
        { value: false, label: localizationService.instant('LanguageService::False') },
      ],
    }))
    .readOnly()
    .build(),
];

export const REGISTER_CONFIG = {
  entityName: 'Register',
  localizationKey: 'Planning::Entity.Register',
  permission: 'PlanningGeography.Registers',
  fields: REGISTER_FIELDS,

  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.Planning + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.Geography + MENU_NAME_SUFFIX,
};

export const ACTION_IDS = {
  importExcelModal: 0,
  downloadExcelTemplateTrigger: 1
};

const languageServiceResource = ZendraLocalizationServices.LanguageService;

function createImportExcelErrorsModal(
  result: ExcelImportResultLike<unknown> & { fileName?: string }
): IModal {
  return createImportExcelModalConfig({
    component: ZnExcelImportErrorsModal,
    title: `${languageServiceResource}::Action:ExcelImport.Title`,
    subTitle: 'Planning::Entity.Register',
    confirmLabel: `${languageServiceResource}::Confirm`,
    dismissLabel: `${languageServiceResource}::Close`,
    size: 'lg',
    inputs: {
      fileName: result.fileName ?? '',
      errors: result.errors,
      title: `${languageServiceResource}::Action:ExcelImport.Title`,
      subTitle: 'Planning::Entity.Register',
      exportErrors: {
        useFactory: (service: RegisterService) => (input: ExcelImportErrorsExportLike) =>
          service.exportRegisterValueImportErrors(input),
        deps: [RegisterService],
      },
    },
    afterClosed: modalResult => {
      if ((modalResult as { retry?: boolean } | undefined)?.retry) {
        return createRegisterImportModal();
      }

      return undefined;
    },
  });
}

function createRegisterImportModal(): IModal {
  return createImportExcelModalConfig({
    component: ZnFileUploader,
    title: `${languageServiceResource}::Action:ExcelImport.Title`,
    subTitle: 'Planning::Entity.Register',
    inputs: {
      importFile: {
        useFactory: (service: RegisterService) => (formData: FormData) =>
          service.importRegisterValues(formData),
        deps: [RegisterService],
      },
    },
    afterClosed: modalResult => {
      const result = modalResult as (ExcelImportResultLike<unknown> & { fileName?: string }) | undefined;
      if (result?.hasErrors) {
        return createImportExcelErrorsModal(result);
      }

      return undefined;
    },
  });
}

const importModalConfig = signal<IModal>(createRegisterImportModal());

export const REGISTER_ACTIONS: IActionsSchema = {
  overTable: [
    modalAction(
      ACTION_IDS.importExcelModal,
      'Action:ExcelImport',
      ActionOverRecordTypes.OverTable,
      importModalConfig(),
      ZendraLocalizationServices.LanguageService,
    ),
    triggerAction(
      ACTION_IDS.downloadExcelTemplateTrigger,
      'Actions:ExcelTemplateDownload',
      ActionOverRecordTypes.OverTable,
      ZendraLocalizationServices.LanguageService,
    ),
  ]
}
